import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronDown, ChevronRight, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useShell } from "@/components/layout/ShellContext";
import { NAV, activeLeaf } from "@/components/layout/nav";
import { Avatar } from "@/components/ui";
import { ROLE_LABEL } from "@/lib/format";
import { cn } from "@/lib/utils";
import logo from "@/assets/logo.png";
import logoMark from "@/assets/logo-mark.png";

const linkClass = (active, extra) => cn("sidebar-link w-full", active ? "sidebar-link-active" : "sidebar-link-inactive", extra);
const iconClass = (active) => (active ? "text-brand-orange" : "text-slate-500");

function Leaf({ item, active, small, collapsed, onNavigate }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      onClick={onNavigate}
      title={collapsed ? item.label : undefined}
      className={linkClass(active, cn(small && "text-xs", collapsed && "justify-center px-2"))}
    >
      <Icon size={small ? 15 : 17} strokeWidth={active ? 2.4 : 1.9} className={iconClass(active)} />
      {!collapsed && (
        <>
          <span className="flex-1 text-left truncate">{item.label}</span>
          {active && <ChevronRight size={13} className="ml-auto text-brand-orange" />}
        </>
      )}
    </NavLink>
  );
}

export default function Sidebar() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const { sidebarOpen, setSidebarOpen, sidebarCollapsed: collapsed } = useShell();
  const items = NAV[user.role] || [];
  const current = activeLeaf(items, pathname);

  const groupOf = (to) => items.find((i) => i.children?.some((c) => c.to === to))?.id;
  const [expanded, setExpanded] = useState(() => ({ [groupOf(current?.to)]: true }));

  // Open the group that owns the active page, close the others (admin-panel behaviour).
  useEffect(() => {
    const id = groupOf(current?.to);
    setExpanded(id ? { [id]: true } : {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.to]);

  const close = () => setSidebarOpen(false);

  const nav = items.map((item) => {
    if (!item.children) {
      return <Leaf key={item.to} item={item} active={current?.to === item.to} collapsed={collapsed} onNavigate={close} />;
    }

    const Icon = item.icon;
    const owns = item.children.some((c) => c.to === current?.to);

    if (collapsed) {
      // Icon with a hover flyout listing the group's pages.
      return (
        <div key={item.id} className="relative group/flyout">
          <NavLink to={item.children[0].to} title={item.label} className={linkClass(owns, "justify-center px-2")}>
            <Icon size={17} strokeWidth={owns ? 2.4 : 1.9} className={iconClass(owns)} />
          </NavLink>
          <div className="absolute left-full top-0 ml-2 z-50 hidden group-hover/flyout:block">
            <div className="bg-surface rounded-xl py-2 min-w-[190px] shadow-neu-sm">
              <p className="px-3 pb-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">{item.label}</p>
              {item.children.map((c) => (
                <Leaf key={c.to} item={c} active={current?.to === c.to} small onNavigate={close} />
              ))}
            </div>
          </div>
        </div>
      );
    }

    const open = !!expanded[item.id];
    const highlight = owns && !open;
    return (
      <div key={item.id}>
        <button onClick={() => setExpanded((e) => ({ ...e, [item.id]: !open }))} className={linkClass(highlight)} aria-expanded={open}>
          <Icon size={17} strokeWidth={highlight ? 2.4 : 1.9} className={iconClass(highlight)} />
          <span className="flex-1 text-left">{item.label}</span>
          {open
            ? <ChevronDown size={14} className="ml-auto text-brand-orange" />
            : <ChevronRight size={14} className={cn("ml-auto", highlight ? "text-brand-orange" : "text-slate-400")} />}
        </button>
        {open && (
          <div className="mt-1 ml-3 pl-3 space-y-1 border-l border-slate-200/70">
            {item.children.map((c) => (
              <Leaf key={c.to} item={c} active={current?.to === c.to} small onNavigate={close} />
            ))}
          </div>
        )}
      </div>
    );
  });

  return (
    <>
      {sidebarOpen && <div className="overlay lg:hidden" onClick={close} />}
      <aside
        className={cn(
          "fixed top-0 left-0 h-full bg-surface z-50 shadow-sidebar flex flex-col transition-all duration-300 ease-in-out",
          sidebarOpen ? "translate-x-0" : "-translate-x-full",
          "lg:translate-x-0 lg:static lg:h-screen lg:shrink-0",
          collapsed ? "w-16" : "w-56",
        )}
      >
        <div className={cn("flex items-center h-12 shrink-0", collapsed ? "justify-center px-2" : "justify-between px-4")}>
          {collapsed
            ? <img src={logoMark} alt="MediLearn" className="h-8 w-8 rounded-md select-none" draggable={false} />
            : <img src={logo} alt="MediLearn" className="h-8 w-auto select-none" draggable={false} />}
          <button onClick={close} className="lg:hidden p-1.5 rounded-lg text-slate-400 hover:text-brand-blue" aria-label="Close menu"><X size={16} /></button>
        </div>


        <nav className={cn("flex-1 py-3 px-3 space-y-1.5", collapsed ? "overflow-visible" : "overflow-y-auto")}>{nav}</nav>

        <div className={cn("px-3 pb-4", collapsed && "flex justify-center")}>
          {collapsed ? <Avatar name={user.name} brand /> : (
            <div className="neu-card-flat p-3 flex items-center gap-3">
              <Avatar name={user.name} brand />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-brand-blue truncate leading-tight">{user.name}</p>
                <span className="text-[10px] font-semibold text-slate-500">{ROLE_LABEL[user.role]}</span>
              </div>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
